import { z } from 'zod';

// PRD §9 user goals
export const goalSchema = z.enum([
  'stress',
  'sleep',
  'focus',
  'meditation',
  'emotional-balance',
  'grounding',
  'recovery',
  'energy',
  'abundance',
  'radiance',
]);

export const consentsSchema = z.object({
  analytics: z.boolean(),
  improveQi: z.boolean(),
  sessionReplay: z.boolean(),
});

/** Text-size preset — drives the root --font-scale (1 / 1.15 / 1.3). */
export const fontScaleSchema = z.enum(['default', 'large', 'larger']);

/**
 * Body for PATCH /v1/me. All fields optional, but at least one must be
 * present so an empty PATCH is rejected.
 */
export const profileUpdateSchema = z
  .object({
    goal: goalSchema.optional(),
    consents: consentsSchema.optional(),
    fontScale: fontScaleSchema.optional(),
  })
  .refine((v) => v.goal !== undefined || v.consents !== undefined || v.fontScale !== undefined, {
    message: 'Provide at least one of goal, consents, or fontScale',
  });

export const planSchema = z.enum(['free', 'premium']);

export const playbackStyleSchema = z.enum(['ambient', 'pulsed-harmonic', 'binaural']);

export const consentFlagsSchema = z.object({
  analytics: z.boolean().default(true), // on by default; opt out in Settings
  improveQi: z.boolean().default(false),
  sessionReplay: z.boolean().default(false),
});

export const userSchema = z.object({
  id: z.string(),
  email: z.email().nullable(),
  name: z.string().nullable(),
  plan: planSchema,
  createdAt: z.iso.datetime(),
});

export const profileSchema = z.object({
  userId: z.string(),
  goal: goalSchema.nullable(),
  locale: z.string().default('en'),
  consents: consentFlagsSchema,
  fontScale: fontScaleSchema.default('default'),
});

// PRD §19 — Frequency model
export const frequencySchema = z.object({
  id: z.string(),
  frequency: z.number().nonnegative(),
  category: z.string(),
  tags: z.array(z.string()),
  type: z.enum(['audible', 'symbolic']),
  historicalAssociation: z.string().optional(),
});

// PRD §16 / SRS §5.5 — resonance peak
export const resonancePeakSchema = z.object({
  frequencyId: z.string(),
  score: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  deltaBreakdown: z.object({
    facial: z.number(),
    blink: z.number(),
    breathing: z.number(),
    voice: z.number(),
    engagement: z.number(),
  }),
});

// PRD §22 — wellness scores (0–100)
export const wellnessScoresSchema = z.object({
  resonance: z.number().min(0).max(100),
  stressLoad: z.number().min(0).max(100),
  emotionalCoherence: z.number().min(0).max(100),
  focus: z.number().min(0).max(100),
  recovery: z.number().min(0).max(100),
  energy: z.number().min(0).max(100),
});

export const protocolStepSchema = z.object({
  frequencyId: z.string(),
  durationMs: z.number().int().positive(),
  playbackStyle: playbackStyleSchema,
  intent: z.string(),
});

// SRS §5.2 face metrics summary
export const faceSummarySchema = z.object({
  blinkRate: z.number(),
  facialStability: z.number(),
  breathingRateHz: z.number(),
  jawTension: z.number(),
  foreheadTension: z.number(),
  /** Mean apparent pupil/iris diameter over the window (gaze-fixation read). */
  pupilResponse: z.number().optional(),
  framesAnalyzed: z.number().int(),
});

// SRS §5.3 voice metrics summary
export const voiceSummarySchema = z.object({
  pitchHz: z.number(),
  jitter: z.number(),
  shimmer: z.number(),
  stressScore: z.number().min(0).max(100),
  stability: z.number().min(0).max(100),
  breathiness: z.number().min(0).max(100),
});

// SRS §8 — Scan
export const scanSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  startedAt: z.iso.datetime(),
  durationMs: z.number().int().positive(),
  faceMetrics: faceSummarySchema,
  voiceMetrics: voiceSummarySchema,
  resonancePeaks: z.array(resonancePeakSchema),
  scores: wellnessScoresSchema,
});

// SRS §8 — Session
export const sessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  scanId: z.string(),
  protocol: z.array(protocolStepSchema),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  rating: z.number().int().min(1).max(5).optional(),
});

// API response shapes
export const meResponseSchema = z.object({
  user: userSchema.nullable(),
  profile: profileSchema.nullable(),
});

export const frequencyListResponseSchema = z.object({
  frequencies: z.array(frequencySchema),
});

export const scanListResponseSchema = z.object({
  scans: z.array(scanSchema),
});

export const sessionListResponseSchema = z.object({
  sessions: z.array(sessionSchema),
});

export const recommendationResponseSchema = z.object({
  protocol: z.array(protocolStepSchema),
  rationale: z.string(),
});

// TCM tongue reading — a tradition-flavored pattern read derived from the
// tongue photo by a vision model. Never a medical/diagnostic claim. The enums
// double as the vision model's structured-output schema (see /v1/tongue/analyze).
export const tongueBodyColorSchema = z.enum(['pale', 'pink', 'red', 'purple', 'mixed']);
export const tongueCoatingColorSchema = z.enum(['none', 'white', 'yellow', 'mixed']);
export const tongueCoatingThicknessSchema = z.enum(['thin', 'moderate', 'thick']);
export const tongueMoistureSchema = z.enum(['dry', 'balanced', 'moist']);
export const tongueConfidenceSchema = z.enum(['low', 'medium', 'high']);

// A single interpreted region of the face or tongue. In TCM reading each area
// traditionally associates with a body system (面診 face map / 舌診 tongue map);
// this captures what stands out in one area and the system it maps to.
// Association only — never diagnostic.
export const bodyRegionReadingSchema = z.object({
  /** The area, e.g. "Under-eyes", "Cheeks" (face) or "Tip", "Sides", "Root" (tongue). */
  region: z.string(),
  /** What stands out there in plain words, e.g. "shadowed / puffy", "slightly dry". */
  observation: z.string(),
  /** The body system that area traditionally maps to, e.g. "Kidney / fluids". */
  system: z.string(),
  /**
   * 0–100 health read for that system's area — 100 = looks balanced/healthy,
   * lower = more stands out. Optional so older reads without it still parse.
   */
  score: z.number().min(0).max(100).optional(),
});

export const tongueReadingSchema = z.object({
  bodyColor: tongueBodyColorSchema,
  coatingColor: tongueCoatingColorSchema,
  coatingThickness: tongueCoatingThicknessSchema,
  moisture: tongueMoistureSchema,
  /** Observational shape notes (e.g. "teeth-marks", "slight swelling"). May be empty. */
  shapeNotes: z.array(z.string()),
  /**
   * Per-zone interpretation mapping each tongue area to a TCM body system
   * (tip→Heart, sides→Liver, centre→Spleen/Stomach, root→Kidney). May be empty.
   * Optional so older reads persisted before zones existed still parse.
   */
  regions: z.array(bodyRegionReadingSchema).optional(),
  /** One-line tradition-flavored summary, non-diagnostic. */
  summary: z.string(),
  /** The model's confidence in the read — low when the image is poor/unclear. */
  confidence: tongueConfidenceSchema,
});

// TCM facial-complexion reading (面色) — a tradition-flavored read of overall
// face colour + lustre derived from the same photo as the tongue read. The
// BioScan step measures complexion objectively (self-baselined CIE-Lab); this
// is the vision model's tradition-framed interpretation, never diagnostic.
export const faceColorSchema = z.enum(['pale', 'rosy', 'red', 'yellow', 'green', 'dark', 'mixed']);
export const faceLusterSchema = z.enum(['radiant', 'normal', 'dull']);

export const facialComplexionReadingSchema = z.object({
  faceColor: faceColorSchema,
  luster: faceLusterSchema,
  /** Observational notes (e.g. "rosy cheeks", "shadowed under-eyes"). May be empty. */
  areaNotes: z.array(z.string()),
  /** Per-region interpretation mapping each notable area to a TCM body system. May be empty. */
  regions: z.array(bodyRegionReadingSchema),
  /** One-line tradition-flavored summary, non-diagnostic. */
  summary: z.string(),
  /** The model's confidence in the read — low when the image is poor/unclear. */
  confidence: tongueConfidenceSchema,
});

// TCM synthesis — a single plain-language read that combines the tongue + face
// complexion into a yin/yang balance picture: what looks like too much, what
// looks lacking, and one gentle focus for rebalancing. Tradition-framed and
// non-diagnostic; this is the "what does it all mean" summary for the user.
export const yinYangSchema = z.enum(['yin', 'balanced', 'yang']);

export const tcmInterpretationSchema = z.object({
  /** Overall yin/yang lean of the combined read. */
  yinYang: yinYangSchema,
  /** One plain-language sentence describing the overall pattern in simple terms. */
  summary: z.string(),
  /** Short plain phrases for what reads as too much/excess (e.g. "inner heat"). May be empty. */
  excess: z.array(z.string()),
  /** Short plain phrases for what reads as lacking/deficient (e.g. "moisture"). May be empty. */
  deficient: z.array(z.string()),
  /** One gentle, actionable focus for bringing things back to balance. */
  balanceFocus: z.string(),
  /** 0–100 yin/yang harmony — 100 = evenly balanced, lower = strongly one-sided. */
  balance: z.number().min(0).max(100),
  /** 0–100 vitality — complexion lustre + tongue health read as overall vigour. */
  vitality: z.number().min(0).max(100),
  /** 0–100 thermal balance — 100 = comfortably neither overheated nor too cold. */
  heat: z.number().min(0).max(100),
  /** 0–100 hydration — 100 = tongue + skin read well-moistened, not dry. */
  fluids: z.number().min(0).max(100),
  /** 0–100 circulation — 100 = colour + vitality move freely, no stasis/pallor. */
  circulation: z.number().min(0).max(100),
  /** 0–100 organ harmony — 100 = facial zones + tongue map evenly across systems. */
  organHarmony: z.number().min(0).max(100),
});

export const tongueAnalyzeRequestSchema = z.object({
  /** data: URL of the captured tongue photo (image/jpeg or image/png). */
  image: z.string().startsWith('data:image/'),
});

export const tongueAnalyzeResponseSchema = z.object({
  reading: tongueReadingSchema,
  /** LLM TCM facial-complexion read from the same photo. */
  complexion: facialComplexionReadingSchema,
  /** Combined plain-language TCM interpretation (yin/yang + what to balance). */
  interpretation: tcmInterpretationSchema,
});
